import { useEffect, useRef, useState } from "react";
import { CircleAlert, CircleCheck, Info, X, type LucideIcon } from "lucide-react";
import { useRelease150 } from "../../lib/release150";
import { dismissToast, useToasts, type ToastItem, type ToastTone } from "./toast";

// Renders the global toasts (ported from Summit Engineering's Toaster; mounted once by the app shell, release 1.5.0 only).
// Two live regions: successes and information are polite; errors are assertive so a failed save is never missed.
// Hovering or focusing a toast pauses its timer, so nobody has to race to read it or reach its Undo. Motion: the design
// system's 150 ms fade-in, only when motion is allowed (motion-safe + the reduce-motion class stops it).

const TONE: Record<ToastTone, { icon: LucideIcon; label: string; className: string; iconClass: string }> = {
  success: { icon: CircleCheck, label: "Done", className: "border-emerald-200 bg-white", iconClass: "text-emerald-700" },
  info: { icon: Info, label: "Note", className: "border-slate-200 bg-white", iconClass: "text-brand-600" },
  error: { icon: CircleAlert, label: "Error", className: "border-red-200 bg-red-50", iconClass: "text-red-700" }
};

function Toast({ item }: { item: ToastItem }) {
  const meta = TONE[item.tone];
  const Icon = meta.icon;
  const [paused, setPaused] = useState(false);
  const remaining = useRef(item.durationMs);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (item.durationMs <= 0 || paused) return;
    startedAt.current = Date.now();
    const timer = window.setTimeout(() => dismissToast(item.id), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(500, remaining.current - (Date.now() - startedAt.current));
    };
  }, [item.id, item.durationMs, paused]);

  return (
    <li
      className={`pointer-events-auto flex w-80 max-w-[calc(100vw-1.5rem)] items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm shadow-lg motion-safe:animate-fade-in ${meta.className}`}
      data-testid={`toast-${item.tone}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon aria-hidden="true" className={`mt-0.5 h-4 w-4 flex-none ${meta.iconClass}`} />
      <div className="min-w-0 flex-1">
        <p className={`break-words font-medium ${item.tone === "error" ? "text-red-800" : "text-slate-900"}`}>
          <span className="sr-only">{meta.label}: </span>
          {item.title}
        </p>
        {item.detail && <p className={`mt-0.5 break-words text-xs ${item.tone === "error" ? "text-red-800" : "text-slate-600"}`}>{item.detail}</p>}
      </div>
      {item.action && (
        <button
          type="button"
          className="-my-0.5 whitespace-nowrap rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label={item.action.ariaLabel}
          data-testid="toast-action"
          onClick={() => {
            dismissToast(item.id);
            item.action?.onClick();
          }}
        >
          {item.action.label}
        </button>
      )}
      <button
        type="button"
        className="-mr-1 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        onClick={() => dismissToast(item.id)}
        aria-label="Dismiss notification"
      >
        <X aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

/** The toast stack itself (tests render it directly). */
export function ToastStack() {
  const toasts = useToasts();
  const polite = toasts.filter((t) => t.tone !== "error");
  const assertive = toasts.filter((t) => t.tone === "error");
  return (
    <div className="pointer-events-none fixed right-3 top-14 z-[70] flex flex-col items-end gap-2 print:hidden max-md:bottom-20 max-md:top-auto" data-testid="toaster">
      <ul aria-live="assertive" aria-label="Errors" className="flex flex-col items-end gap-2">
        {assertive.map((t) => (
          <Toast key={t.id} item={t} />
        ))}
      </ul>
      <ul aria-live="polite" aria-label="Notifications" className="flex flex-col items-end gap-2">
        {polite.map((t) => (
          <Toast key={t.id} item={t} />
        ))}
      </ul>
    </div>
  );
}

/** Mounted once by the app shell. Release 1.5.0 only: with it off nothing renders and 1.4.0 is unchanged. */
export function Toaster() {
  const release150 = useRelease150();
  if (!release150) return null;
  return <ToastStack />;
}
